import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import GigRequestConversationModal from "../gigs/GigRequestConversationModal";
import Button from "../ui/Button/Button";
import InfoTooltip from "../ui/InfoTooltip/InfoTooltip";
import Modal from "../ui/Modal/Modal";
import PageActionBar from "../ui/Page/PageActionBar";
import PageBackButton from "../ui/Page/PageBackButton";
import PageHero from "../ui/Page/PageHero";
import PageSectionCard from "../ui/Page/PageSectionCard";
import PageShell from "../ui/Page/PageShell";
import Toast, { type ToastTone } from "../ui/Toast/Toast";
import type { GigRequestPaymentStatus, LocationType, ScheduleProposalResponse } from "../../types/gigTypes";
import type { CreateReviewRequest } from "../../types/reviewTypes";
import type { RemoveTeenBlockingReason } from "../../types/scheduledGigTypes";
import { formatDateTime, formatDateTimeInGigTimeZone } from "../../utils/DateUtils";
import { formatGigCompensation, formatTeenGigCompensation } from "../../utils/GigCompensationUtils";
import { formatCityStateZip } from "../../utils/LocationUtils";

export type ScheduledGigCard = {
  requestId: string;
  gigId: string | null;
  gigTitle: string | null;
  gigDescription: string | null;
  gigRequiresEquipment: boolean;
  gigEquipmentDescription: string | null;
  locationType: LocationType;
  city: string | null;
  state: string | null;
  zip: string | null;
  gigTimeZone: string | null;
  participantName: string;
  participantLabel: string;
  paymentAmountCents: number | null;
  volunteer: boolean;
  teensRequired: number;
  coveredSeats: number;
  confirmedStartTime: string | null;
  confirmedEndTime: string | null;
  scheduleProposals: ScheduleProposalResponse[];
  teenArrivedAt: string | null;
  teenMarkedCompletedAt: string | null;
  adultConfirmedCompletedAt: string | null;
  issueReportedAt: string | null;
  issueReportMessage: string | null;
  paymentStatus: GigRequestPaymentStatus | null;
  fundedAt: string | null;
  releasedAt: string | null;
  refundedAt: string | null;
  removeTeenBlockingReason?: RemoveTeenBlockingReason | null;
};

type ToastState = {
  message: string;
  tone: ToastTone;
} | null;
type ReviewMode = "teen" | "adult";

const ratingOptions = [5, 4.5, 4, 3.5, 3, 2.5, 2, 1.5, 1, 0.5, 0];

type ScheduledGigGroup = {
  groupId: string;
  primaryGig: ScheduledGigCard;
  assignments: ScheduledGigCard[];
  teensRequired: number;
  coveredSeats: number;
};

type ScheduledGigsWorkspaceProps = {
  role: "teen" | "adult";
  title: string;
  subtitle: string;
  loadingMessage: string;
  emptyMessage: string;
  gigs: ScheduledGigCard[];
  isLoading: boolean;
  error: string | null;
  onTeenMarkArrived?: (requestId: string) => Promise<void>;
  onTeenMarkComplete?: (requestId: string) => Promise<void>;
  onTeenSubmitReview?: (requestId: string, payload: CreateReviewRequest) => Promise<void>;
  onAdultConfirmComplete?: (requestId: string) => Promise<void>;
  onAdultSubmitReview?: (requestId: string, payload: CreateReviewRequest) => Promise<void>;
  onReportIssue?: (requestId: string, message: string) => Promise<void>;
  onCancel?: (requestId: string, note?: string) => Promise<void>;
  onRemoveTeen?: (requestId: string, note: string) => Promise<void>;
  onLeaveGig?: (requestId: string, note: string) => Promise<void>;
};

const toLocationLine = (gig: ScheduledGigCard) => formatCityStateZip(gig);

const assignmentContext = (gig: Pick<ScheduledGigCard, "teensRequired" | "coveredSeats">) => {
  const teensRequired = Math.max(1, gig.teensRequired || 1);
  const coveredSeats = Math.max(1, gig.coveredSeats || 1);
  if (teensRequired <= 1 && coveredSeats <= 1) {
    return null;
  }
  return `Covers ${coveredSeats} of ${teensRequired} teen seat${teensRequired === 1 ? "" : "s"}`;
};

const isMultiTeenAssignment = (gig: Pick<ScheduledGigCard, "teensRequired" | "coveredSeats">) =>
  assignmentContext(gig) !== null;

const scheduledUnit = (gig: Pick<ScheduledGigCard, "teensRequired" | "coveredSeats">) =>
  isMultiTeenAssignment(gig) ? "scheduled assignment" : "scheduled gig";

const groupKeyForGig = (gig: ScheduledGigCard) => gig.gigId ? `gig:${gig.gigId}` : `request:${gig.requestId}`;

const buildScheduledGigGroups = (gigs: ScheduledGigCard[]): ScheduledGigGroup[] => {
  const groups = new Map<string, ScheduledGigCard[]>();

  for (const gig of gigs) {
    const groupKey = groupKeyForGig(gig);
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), gig]);
  }

  return Array.from(groups.entries()).map(([groupId, assignments]) => {
    const primaryGig = assignments[0];
    const teensRequired = Math.max(1, ...assignments.map((gig) => gig.teensRequired || 1));
    const coveredSeats = assignments.reduce((sum, gig) => sum + Math.max(1, gig.coveredSeats || 1), 0);

    return {
      groupId,
      primaryGig,
      assignments,
      teensRequired,
      coveredSeats,
    };
  });
};

const scheduledGroupContext = (group: ScheduledGigGroup) => {
  if (group.teensRequired <= 1 && group.coveredSeats <= 1) {
    return null;
  }
  return `${Math.min(group.coveredSeats, group.teensRequired)} of ${group.teensRequired} teen seats scheduled`;
};

const groupParticipantSummary = (group: ScheduledGigGroup, role: "teen" | "adult") => {
  if (role === "teen" || group.assignments.length === 1) {
    const gig = group.primaryGig;
    return `${gig.participantLabel}: ${gig.participantName}`;
  }

  return `Teens: ${group.assignments.map((assignment) => assignment.participantName).join(", ")}`;
};

const groupCompletionLabel = (group: ScheduledGigGroup) => {
  if (group.assignments.every((gig) => gig.teenMarkedCompletedAt && gig.adultConfirmedCompletedAt)) {
    return "All complete";
  }
  if (group.assignments.some((gig) => gig.teenMarkedCompletedAt || gig.adultConfirmedCompletedAt)) {
    return "Partly complete";
  }
  return "Scheduled";
};

const groupPaymentAmountCents = (group: ScheduledGigGroup, role: "teen" | "adult") => {
  if (role === "teen" || group.assignments.length === 1) {
    return group.primaryGig.paymentAmountCents;
  }

  return group.assignments.reduce<number | null>((total, assignment) => {
    if (assignment.paymentAmountCents === null) {
      return total;
    }
    return (total ?? 0) + assignment.paymentAmountCents;
  }, null);
};

const assignmentStatusMeta = (assignment: ScheduledGigCard) => {
  if (assignment.teenMarkedCompletedAt && assignment.adultConfirmedCompletedAt) {
    return { label: "Marked Complete", className: "font-semibold text-emerald-700" };
  }
  if (assignment.adultConfirmedCompletedAt) {
    return { label: "Marked Complete", className: "font-semibold text-emerald-700" };
  }
  if (assignment.teenMarkedCompletedAt) {
    return { label: "Teen marked complete", className: "text-myteengig-text-muted" };
  }
  return { label: paymentStatusLabel(assignment.paymentStatus), className: "text-myteengig-text-muted" };
};

const paymentStatusLabel = (status: GigRequestPaymentStatus | null) => {
  switch (status) {
    case "CHECKOUT_PENDING":
      return "Funding pending";
    case "FUNDED":
      return "Funds held";
    case "RELEASE_READY":
      return "Ready to release";
    case "PAID_OUT":
      return "Payout released";
    case "REFUNDED":
      return "Refunded";
    case "PARTIALLY_REFUNDED":
      return "Partially refunded";
    case "PARTIALLY_SETTLED":
      return "Preparation payout sent";
    case "TRANSFER_REVERSED":
      return "Transfer reversed";
    case "FAILED":
      return "Payment failed";
    default:
      return "No Stripe payment";
  }
};

const LATE_CANCELLATION_WINDOW_HOURS = 24;
const ARRIVAL_EARLY_WINDOW_MINUTES = 60;

const startDate = (value: string | null) => (value ? new Date(value) : null);

const scrollToScheduledGig = (requestId: string, attempt = 0) => {
  const targetElement = document.getElementById(`scheduled-gig-${requestId}`);
  if (!targetElement) {
    if (attempt < 8) {
      window.setTimeout(() => scrollToScheduledGig(requestId, attempt + 1), 60);
    }
    return;
  }
  targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
};

const parseWorkspaceSection = (value: string | null): "summary" | "details" | "schedule" | "messages" =>
  value === "details" || value === "schedule" || value === "messages" ? value : "summary";

const hasStarted = (gig: ScheduledGigCard) => {
  const confirmedStart = startDate(gig.confirmedStartTime);
  return confirmedStart !== null && confirmedStart.getTime() <= Date.now();
};

const isWithinLateCancellationWindow = (gig: ScheduledGigCard) => {
  const confirmedStart = startDate(gig.confirmedStartTime);
  if (!confirmedStart) {
    return false;
  }
  const diffMs = confirmedStart.getTime() - Date.now();
  return diffMs > 0 && diffMs <= LATE_CANCELLATION_WINDOW_HOURS * 60 * 60 * 1000;
};

const isArrivalWindowOpen = (gig: ScheduledGigCard) => {
  const confirmedStart = startDate(gig.confirmedStartTime);
  if (!confirmedStart) {
    return false;
  }
  return Date.now() >= confirmedStart.getTime() - ARRIVAL_EARLY_WINDOW_MINUTES * 60 * 1000;
};

const getArrivalBlockingReason = (gig: ScheduledGigCard) => {
  if (gig.teenArrivedAt) {
    return `Arrival has already been recorded for this ${isMultiTeenAssignment(gig) ? "assignment" : "gig"}.`;
  }
  if (!gig.confirmedStartTime) {
    return `Arrival is unavailable until this ${isMultiTeenAssignment(gig) ? "assignment" : "gig"} has a confirmed start time.`;
  }
  if (!isArrivalWindowOpen(gig)) {
    return "Arrival becomes available 60 minutes before the scheduled start time.";
  }
  return null;
};

const getRemoveTeenBlockingReason = (gig: ScheduledGigCard) => {
  const unit = isMultiTeenAssignment(gig) ? "assignment" : "gig";
  if (gig.removeTeenBlockingReason) {
    switch (gig.removeTeenBlockingReason) {
      case "START_TIME_WITHIN_24_HOURS":
        return `You can't remove a teen within a 24 hours window of this ${unit}'s confirmed start time. Cancel the ${unit} instead and include a note so an admin can review.`;
      case "START_TIME_PASSED":
        return `Remove Teen is unavailable after this ${unit}'s scheduled start time.`;
      case "TEEN_MARKED_ARRIVED":
        return `Remove Teen is unavailable after the teen marks arrival for this ${unit}.`;
      case "TEEN_MARKED_COMPLETE":
      case "ADULT_MARKED_COMPLETE":
        return `Remove Teen is unavailable after either side marks this ${unit} complete.`;
      case "OPEN_ISSUE_EXISTS":
        return `Remove Teen is unavailable while an issue is open for this ${unit}.`;
      case "PAYOUT_ALREADY_RELEASED":
        return "Remove Teen is unavailable because payout or settlement has already started for this payment. Use issue review for any remaining payment changes.";
      case "NO_ACTIVE_FUNDED_ASSIGNMENT":
        return `Only paid funded ${unit}s can use Remove Teen.`;
    }
  }
  if (gig.volunteer) {
    return `Only paid funded ${unit}s can use Remove Teen.`;
  }
  if (gig.teenArrivedAt) {
    return `Remove Teen is unavailable after the teen marks arrival for this ${unit}.`;
  }
  if (hasStarted(gig)) {
    return `Remove Teen is unavailable after this ${unit}'s scheduled start time.`;
  }
  if (gig.issueReportedAt) {
    return `Remove Teen is unavailable while an issue is open for this ${unit}.`;
  }
  if (isWithinLateCancellationWindow(gig)) {
    return `You can't remove a teen within a 24 hours window of this ${unit}'s confirmed start time. Cancel the ${unit} instead and include a note so an admin can review.`;
  }
  if (gig.teenMarkedCompletedAt || gig.adultConfirmedCompletedAt) {
    return `Remove Teen is unavailable after either side marks this ${unit} complete.`;
  }
  return null;
};

const getLeaveGigBlockingReason = (gig: ScheduledGigCard) => {
  const unit = isMultiTeenAssignment(gig) ? "assignment" : "gig";
  if (gig.volunteer) {
    return `Only paid funded ${unit}s can use Leave ${isMultiTeenAssignment(gig) ? "Assignment" : "Gig"}.`;
  }
  if (gig.teenArrivedAt) {
    return `Leave ${isMultiTeenAssignment(gig) ? "Assignment" : "Gig"} is unavailable after you mark arrival for this ${unit}.`;
  }
  if (hasStarted(gig)) {
    return `Leave ${isMultiTeenAssignment(gig) ? "Assignment" : "Gig"} is unavailable after this ${unit}'s scheduled start time.`;
  }
  if (gig.issueReportedAt) {
    return `Leave ${isMultiTeenAssignment(gig) ? "Assignment" : "Gig"} is unavailable while an issue is open for this ${unit}.`;
  }
  if (isWithinLateCancellationWindow(gig)) {
    return `You can't leave this ${unit} within a 24 hours window of the confirmed start time. Cancel the ${unit} instead and include a note so an admin can review.`;
  }
  if (gig.teenMarkedCompletedAt || gig.adultConfirmedCompletedAt) {
    return `Leave ${isMultiTeenAssignment(gig) ? "Assignment" : "Gig"} is unavailable after either side marks this ${unit} complete.`;
  }
  return null;
};

const ScheduledGigsWorkspace = ({
  role,
  title,
  subtitle,
  loadingMessage,
  emptyMessage,
  gigs,
  isLoading,
  error,
  onTeenMarkArrived,
  onTeenMarkComplete,
  onTeenSubmitReview,
  onAdultConfirmComplete,
  onAdultSubmitReview,
  onReportIssue,
  onCancel,
  onRemoveTeen,
  onLeaveGig,
}: ScheduledGigsWorkspaceProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [issueDrafts, setIssueDrafts] = useState<Record<string, string>>({});
  const [expandedIssueRequestId, setExpandedIssueRequestId] = useState<string | null>(null);
  const [focusedRequestId, setFocusedRequestId] = useState<string | null>(null);
  const [pendingCancelGig, setPendingCancelGig] = useState<ScheduledGigCard | null>(null);
  const [pendingRemoveTeenGig, setPendingRemoveTeenGig] = useState<ScheduledGigCard | null>(null);
  const [pendingLeaveGig, setPendingLeaveGig] = useState<ScheduledGigCard | null>(null);
  const [pendingReviewGig, setPendingReviewGig] = useState<ScheduledGigCard | null>(null);
  const [pendingReviewMode, setPendingReviewMode] = useState<ReviewMode | null>(null);
  const [reviewRating, setReviewRating] = useState("5");
  const [reviewMessage, setReviewMessage] = useState("");
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [cancelNote, setCancelNote] = useState("");
  const [removeTeenNote, setRemoveTeenNote] = useState("");
  const [leaveGigNote, setLeaveGigNote] = useState("");
  const [toast, setToast] = useState<ToastState>(null);
  const [selectedDetailsGig, setSelectedDetailsGig] = useState<ScheduledGigCard | null>(null);
  const [selectedConversationGig, setSelectedConversationGig] = useState<ScheduledGigCard | null>(null);
  const [selectedActionsGig, setSelectedActionsGig] = useState<ScheduledGigCard | null>(null);
  const pendingRemoveTeenBlockedReason = pendingRemoveTeenGig
    ? getRemoveTeenBlockingReason(pendingRemoveTeenGig)
    : null;
  const pendingLeaveGigBlockedReason = pendingLeaveGig
    ? getLeaveGigBlockingReason(pendingLeaveGig)
    : null;
  const groupedGigs = buildScheduledGigGroups(gigs);
  const selectedDetailsAssignments = selectedDetailsGig
    ? gigs.filter((gig) => groupKeyForGig(gig) === groupKeyForGig(selectedDetailsGig))
    : [];
  const selectedDetailsCoveredSeats = selectedDetailsAssignments.reduce(
    (sum, gig) => sum + Math.max(1, gig.coveredSeats || 1),
    0,
  );

  useEffect(() => {
    const targetRequestId = searchParams.get("requestId");
    if (!targetRequestId || isLoading || gigs.length === 0) {
      return;
    }

    if (!gigs.some((gig) => gig.requestId === targetRequestId)) {
      return;
    }

    const targetSection = parseWorkspaceSection(searchParams.get("section"));
    setFocusedRequestId(targetRequestId);
    const targetGig = gigs.find((gig) => gig.requestId === targetRequestId) ?? null;
    if (targetSection === "details") {
      setSelectedDetailsGig(targetGig);
      setSelectedConversationGig(null);
    } else if (targetSection === "messages" || targetSection === "schedule") {
      setSelectedConversationGig(targetGig);
      setSelectedDetailsGig(null);
    } else {
      setSelectedDetailsGig(null);
      setSelectedConversationGig(null);
    }
    scrollToScheduledGig(targetRequestId);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("requestId");
    nextParams.delete("section");
    setSearchParams(nextParams, { replace: true });
  }, [gigs, isLoading, searchParams, setSearchParams]);

  useEffect(() => {
    if (selectedDetailsGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === selectedDetailsGig.requestId) ?? null;
      setSelectedDetailsGig(refreshedGig);
    }
    if (selectedConversationGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === selectedConversationGig.requestId) ?? null;
      setSelectedConversationGig(refreshedGig);
    }
    if (selectedActionsGig) {
      const refreshedGig = gigs.find((gig) => gig.requestId === selectedActionsGig.requestId) ?? null;
      setSelectedActionsGig(refreshedGig);
    }
  }, [gigs, selectedActionsGig, selectedConversationGig, selectedDetailsGig]);

  const handleTeenComplete = async (requestId: string) => {
    if (!onTeenMarkComplete) {
      return;
    }

    const gig = gigs.find((item) => item.requestId === requestId) ?? null;
    setPendingRequestId(requestId);
    try {
      await onTeenMarkComplete(requestId);
      if (gig) {
        setSelectedActionsGig(null);
        openReviewModal({
          ...gig,
          teenMarkedCompletedAt: gig.teenMarkedCompletedAt ?? new Date().toISOString(),
        }, "teen");
      }

      setToast({ tone: "success", message: "Your completion mark was saved." });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to update gig." });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleTeenArrival = async (requestId: string) => {
    if (!onTeenMarkArrived) {
      return;
    }

    setPendingRequestId(requestId);
    try {
      await onTeenMarkArrived(requestId);
      setSelectedActionsGig(null);
      setToast({ tone: "success", message: "Your arrival was recorded." });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to record arrival." });
    } finally {
      setPendingRequestId(null);
    }
  };

  const openReviewModal = (gig: ScheduledGigCard, mode: ReviewMode) => {
    setPendingReviewGig(gig);
    setPendingReviewMode(mode);
    setReviewRating("5");
    setReviewMessage("");
    setReviewError(null);
  };

  const closeReviewModal = () => {
    if (pendingReviewGig && pendingRequestId === pendingReviewGig.requestId) {
      return;
    }

    setPendingReviewGig(null);
    setPendingReviewMode(null);
    setReviewRating("5");
    setReviewMessage("");
    setReviewError(null);
  };

  const submitReview = async (gig: ScheduledGigCard, mode: ReviewMode, reviewPayload: CreateReviewRequest) => {
    const submitter = mode === "teen" ? onTeenSubmitReview : onAdultSubmitReview;
    if (!submitter) {
      return;
    }

    const requestId = gig.requestId;

    setPendingRequestId(requestId);
    setReviewError(null);

    try {
      await submitter(requestId, reviewPayload);

      closeReviewModal();
      setToast({ tone: "success", message: "Your review was submitted." });
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to submit review.";
      setReviewError(message);
      setToast({ tone: "error", message });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleAdultConfirm = async (requestId: string) => {
    if (!onAdultConfirmComplete) {
      return;
    }

    const gig = gigs.find((item) => item.requestId === requestId) ?? null;
    setPendingRequestId(requestId);
    try {
      await onAdultConfirmComplete(requestId);
      if (gig) {
        setSelectedActionsGig(null);
        openReviewModal({
          ...gig,
          adultConfirmedCompletedAt: gig.adultConfirmedCompletedAt ?? new Date().toISOString(),
        }, "adult");
      }

      setToast({ tone: "success", message: "Your completion mark was saved." });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to confirm gig." });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleReportIssue = async (requestId: string) => {
    if (!onReportIssue) {
      return;
    }

    const gig = gigs.find((item) => item.requestId === requestId) ?? null;
    const message = issueDrafts[requestId]?.trim() ?? "";
    if (!message) {
      setToast({ tone: "error", message: "Add an issue note before submitting." });
      return;
    }

    setPendingRequestId(requestId);
    try {
      await onReportIssue(requestId, message);
      setExpandedIssueRequestId(null);
      setSelectedActionsGig(null);
      setIssueDrafts((current) => ({ ...current, [requestId]: "" }));
      setToast({ tone: "success", message: `Issue reported for this ${gig && isMultiTeenAssignment(gig) ? "assignment" : "gig"}.` });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to report issue." });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleCancel = async () => {
    if (!onCancel || !pendingCancelGig) {
      return;
    }

    const trimmedNote = cancelNote.trim();
    const requiresLateCancellationNote = isWithinLateCancellationWindow(pendingCancelGig);
    if (requiresLateCancellationNote && trimmedNote.length === 0) {
      setToast({ tone: "error", message: "Add a cancellation note for admin review." });
      return;
    }

    setPendingRequestId(pendingCancelGig.requestId);
    try {
      await onCancel(pendingCancelGig.requestId, trimmedNote.length > 0 ? trimmedNote : undefined);
      setPendingCancelGig(null);
      setCancelNote("");
      setToast({
        tone: "success",
        message:
          requiresLateCancellationNote
            ? "Cancellation request sent to admin review."
            : pendingCancelGig.volunteer
              ? `${isMultiTeenAssignment(pendingCancelGig) ? "Assignment" : "Gig"} cancelled.`
              : `${isMultiTeenAssignment(pendingCancelGig) ? "Assignment" : "Gig"} cancelled and refunded.`,
      });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to cancel gig." });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleLeaveGig = async () => {
    if (!onLeaveGig || !pendingLeaveGig) {
      return;
    }
    const trimmedNote = leaveGigNote.trim();
    if (trimmedNote.length === 0) {
      setToast({ tone: "error", message: "Add a note before leaving this gig." });
      return;
    }

    setPendingRequestId(pendingLeaveGig.requestId);
    try {
      await onLeaveGig(pendingLeaveGig.requestId, trimmedNote);
      setPendingLeaveGig(null);
      setLeaveGigNote("");
      setToast({
        tone: "success",
        message: isMultiTeenAssignment(pendingLeaveGig)
          ? "You left this assignment and the spot was reopened."
          : "You left the gig and it was reopened.",
      });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to leave gig." });
    } finally {
      setPendingRequestId(null);
    }
  };

  const handleRemoveTeen = async () => {
    if (!onRemoveTeen || !pendingRemoveTeenGig) {
      return;
    }
    const trimmedNote = removeTeenNote.trim();
    if (trimmedNote.length === 0) {
      setToast({ tone: "error", message: "Add a removal note before removing the teen." });
      return;
    }

    setPendingRequestId(pendingRemoveTeenGig.requestId);
    try {
      await onRemoveTeen(pendingRemoveTeenGig.requestId, trimmedNote);
      setPendingRemoveTeenGig(null);
      setRemoveTeenNote("");
      setToast({
        tone: "success",
        message: isMultiTeenAssignment(pendingRemoveTeenGig)
          ? "Teen removed from this assignment and the spot was reopened."
          : "Teen removed and gig reopened.",
      });
    } catch (requestError) {
      setToast({ tone: "error", message: requestError instanceof Error ? requestError.message : "Failed to remove teen." });
    } finally {
      setPendingRequestId(null);
    }
  };

  return (
    <PageShell width="6xl">
      <Toast
        isOpen={toast !== null}
        tone={toast?.tone ?? "info"}
        message={toast?.message ?? ""}
        onClose={() => setToast(null)}
      />
      <PageBackButton fallbackTo="/dashboard" label="Back to Dashboard" className="mb-4" />
      <PageHero
        eyebrow={role === "teen" ? "Teen Workspace" : undefined}
        title={title}
        description={subtitle}
        className="bg-gradient-to-br from-white via-myteengig-background to-white"
      />

      {isLoading ? <p className="mt-8 text-myteengig-text-muted">{loadingMessage}</p> : null}
      {error ? <p className="mt-8 text-sm font-medium text-red-600">{error}</p> : null}

      {!isLoading && !error && gigs.length === 0 ? (
        <PageSectionCard className="mt-8 border-dashed bg-white/80 p-8 text-center">
          <h2 className="text-xl font-semibold text-myteengig-text-primary">No scheduled gigs here</h2>
          <p className="mt-2 text-sm text-myteengig-text-muted">{emptyMessage}</p>
        </PageSectionCard>
      ) : null}

      {!isLoading && !error && groupedGigs.length > 0 ? (
        <div className="mt-6 grid gap-3 sm:mt-8 sm:gap-6">
          {groupedGigs.map((group) => {
            const gig = group.primaryGig;
            const isFocused = group.assignments.some((assignment) => focusedRequestId === assignment.requestId);
            const groupContextLabel = scheduledGroupContext(group);
            const hasReportedIssue = group.assignments.some((assignment) => assignment.issueReportedAt);
            const isMultiTeenGroup = group.assignments.length > 1 || group.teensRequired > 1;

            return (
              <article
                key={group.groupId}
                id={!isMultiTeenGroup ? `scheduled-gig-${gig.requestId}` : undefined}
                className={`overflow-hidden rounded-[1.75rem] border bg-white shadow-sm ${
                  isFocused ? "border-myteengig-primary ring-2 ring-myteengig-primary/20" : "border-myteengig-border"
                }`}
              >
                <div className="bg-[radial-gradient(circle_at_top_left,_rgba(163,230,53,0.18),_transparent_38%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(249,250,251,1))] p-3 sm:p-6">
                  <div className="flex flex-col gap-3 sm:gap-5 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 max-w-3xl">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <h2 className="w-full text-lg font-semibold leading-6 text-myteengig-text-primary sm:w-auto sm:text-2xl sm:leading-8">
                          {gig.gigTitle ?? "Untitled Gig"}
                        </h2>
                        <span className="rounded-full border border-myteengig-border bg-white/90 px-2 py-0.5 text-xs font-semibold text-myteengig-text-primary shadow-sm sm:px-3 sm:py-1 sm:text-sm">
                          {role === "teen"
                            ? formatTeenGigCompensation(gig.paymentAmountCents, gig.volunteer, "Not set")
                            : formatGigCompensation(groupPaymentAmountCents(group, role), gig.volunteer, "Not set")}
                        </span>
                        <span className="rounded-full border border-myteengig-border bg-white px-2 py-0.5 text-[11px] font-semibold leading-5 text-myteengig-text-primary sm:px-3 sm:py-1 sm:text-xs">
                          {groupCompletionLabel(group)}
                        </span>
                        {hasReportedIssue ? (
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold leading-5 text-amber-900 sm:px-3 sm:py-1 sm:text-xs">
                            Issue reported
                          </span>
                        ) : null}
                        {groupContextLabel ? (
                          <span className="rounded-full border border-myteengig-border bg-white px-2 py-0.5 text-[11px] font-semibold leading-5 text-myteengig-text-muted sm:px-3 sm:py-1 sm:text-xs">
                            {groupContextLabel}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-2 line-clamp-2 text-sm leading-5 text-myteengig-text-muted sm:mt-3 sm:line-clamp-none sm:leading-6">
                        {gig.gigDescription ?? "No description yet."}
                      </p>
                      {gig.gigRequiresEquipment ? (
                        <p className="mt-2 inline-flex rounded-full border border-myteengig-border bg-white/90 px-2 py-0.5 text-[11px] font-semibold leading-5 text-myteengig-text-primary shadow-sm sm:mt-3 sm:px-3 sm:py-1 sm:text-xs">
                          Equipment required
                        </p>
                      ) : null}
                      <div className="mt-2 space-y-1.5 sm:mt-3 sm:space-y-3">
                        <p className="text-sm font-medium leading-5 text-myteengig-text-primary sm:leading-6">
                          {formatDateTimeInGigTimeZone(gig.confirmedStartTime, gig.gigTimeZone)} · {groupParticipantSummary(group, role)}
                        </p>
                        <p className="hidden text-sm text-myteengig-text-muted sm:block">
                          {isMultiTeenGroup
                            ? "Use each teen row to message or manage that teen's assignment."
                            : "Use View Details or Message / Schedule for the full gig information."}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 lg:flex lg:min-w-[220px] lg:flex-col lg:gap-3 lg:self-start">
                      <Button
                        variant="outline"
                        size="xs"
                        className="w-full justify-center !px-2 !py-1.5 !text-xs lg:!py-2 lg:!text-sm"
                        aria-label="View Details"
                        onClick={() => setSelectedDetailsGig(gig)}
                      >
                        <span className="lg:hidden">Details</span>
                        <span className="hidden lg:inline">View Details</span>
                      </Button>
                      {!isMultiTeenGroup ? (
                        <>
                          <Button
                            size="xs"
                            className="w-full justify-center !px-2 !py-1.5 !text-xs lg:!py-2 lg:!text-sm"
                            aria-label="Message / Schedule"
                            onClick={() => setSelectedConversationGig(gig)}
                          >
                            <span className="lg:hidden">Message</span>
                            <span className="hidden lg:inline">Message / Schedule</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="xs"
                            className="w-full justify-center !px-2 !py-1.5 !text-xs lg:!py-2 lg:!text-sm"
                            onClick={() => {
                              setSelectedActionsGig(gig);
                              setExpandedIssueRequestId(null);
                            }}
                          >
                            Actions
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {isMultiTeenGroup ? (
                    <div className="mt-4 divide-y divide-myteengig-border rounded-2xl border border-myteengig-border bg-white/90">
                      {group.assignments.map((assignment) => {
                        const assignmentIsPending = pendingRequestId === assignment.requestId;
                        const assignmentStatus = assignmentStatusMeta(assignment);
                        return (
                          <div
                            key={assignment.requestId}
                            id={`scheduled-gig-${assignment.requestId}`}
                            className="grid gap-3 px-3 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-4"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-myteengig-text-primary">
                                {assignment.participantName}
                              </p>
                              <p className={`mt-1 text-xs ${assignmentStatus.className}`}>
                                {assignmentStatus.label}
                              </p>
                              {assignment.issueReportedAt ? (
                                <p className="mt-1 text-xs font-medium text-amber-900">Issue reported</p>
                              ) : null}
                            </div>
                            <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                              <Button
                                size="xs"
                                className="w-full justify-center !px-2 !py-1.5 !text-xs sm:w-auto"
                                disabled={assignmentIsPending}
                                onClick={() => setSelectedConversationGig(assignment)}
                              >
                                Message
                              </Button>
                              <Button
                                variant="outline"
                                size="xs"
                                className="w-full justify-center !px-2 !py-1.5 !text-xs sm:w-auto"
                                disabled={assignmentIsPending}
                                onClick={() => {
                                  setSelectedActionsGig(assignment);
                                  setExpandedIssueRequestId(null);
                                }}
                              >
                                Actions
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      <Modal
        isOpen={selectedDetailsGig !== null}
        onClose={() => setSelectedDetailsGig(null)}
        title={selectedDetailsGig?.gigTitle ?? "Gig Details"}
        panelClassName="max-w-5xl"
      >
        {selectedDetailsGig ? (
          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <div className="rounded-2xl bg-myteengig-background px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Scheduled</p>
              <p className="mt-2 text-sm font-medium text-myteengig-text-primary">{formatDateTimeInGigTimeZone(selectedDetailsGig.confirmedStartTime, selectedDetailsGig.gigTimeZone)}</p>
              <p className="mt-1 text-sm text-myteengig-text-muted">Ends {formatDateTimeInGigTimeZone(selectedDetailsGig.confirmedEndTime, selectedDetailsGig.gigTimeZone)}</p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">
                {selectedDetailsAssignments.length > 1 && role === "adult" ? "Teens" : selectedDetailsGig.participantLabel}
              </p>
              {selectedDetailsAssignments.length > 1 && role === "adult" ? (
                <div className="mt-2 space-y-1">
                  {selectedDetailsAssignments.map((assignment) => (
                    <p key={assignment.requestId} className="text-sm font-medium text-myteengig-text-primary">
                      {assignment.participantName}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-sm font-medium text-myteengig-text-primary">{selectedDetailsGig.participantName}</p>
              )}
            </div>
            {assignmentContext(selectedDetailsGig) ? (
              <div className="rounded-2xl bg-myteengig-background px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">
                  {selectedDetailsAssignments.length > 1 && role === "adult" ? "Crew" : "Assignment"}
                </p>
                <p className="mt-2 text-sm font-medium text-myteengig-text-primary">
                  {selectedDetailsAssignments.length > 1 && role === "adult"
                    ? `${Math.min(selectedDetailsCoveredSeats, selectedDetailsGig.teensRequired)} of ${selectedDetailsGig.teensRequired} teen seats scheduled`
                    : assignmentContext(selectedDetailsGig)}
                </p>
              </div>
            ) : null}
            <div className="rounded-2xl bg-myteengig-background px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Arrival</p>
              <p className="mt-2 text-sm text-myteengig-text-primary">
                {selectedDetailsGig.teenArrivedAt ? `Marked arrived ${formatDateTime(selectedDetailsGig.teenArrivedAt)}` : "Not marked yet"}
              </p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-3">
              <div className="flex items-center justify-center gap-1">
                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Completion</p>
                <InfoTooltip
                  placement="top-left"
                  message={`This shows whether each side has marked this ${isMultiTeenAssignment(selectedDetailsGig) ? "scheduled assignment" : "scheduled gig"} complete. It is fully completed only after both the teen and the adult mark it complete.`}
                />
              </div>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-myteengig-text-primary">
                  <span className="font-medium">Teen:</span>{" "}
                  {selectedDetailsGig.teenMarkedCompletedAt ? `Marked complete ${formatDateTime(selectedDetailsGig.teenMarkedCompletedAt)}` : "Not marked yet"}
                </p>
                <p className="text-sm text-myteengig-text-primary">
                  <span className="font-medium">Adult:</span>{" "}
                  {selectedDetailsGig.adultConfirmedCompletedAt ? `Marked complete ${formatDateTime(selectedDetailsGig.adultConfirmedCompletedAt)}` : "Not marked yet"}
                </p>
              </div>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">
                {isMultiTeenAssignment(selectedDetailsGig) ? "Assignment payment status" : "Payment status"}
              </p>
              <p className="mt-2 font-medium text-myteengig-text-primary">{paymentStatusLabel(selectedDetailsGig.paymentStatus)}</p>
              <p className="mt-1 text-sm text-myteengig-text-muted">
                {selectedDetailsGig.refundedAt
                  ? `Refunded on ${formatDateTime(selectedDetailsGig.refundedAt)}`
                  : selectedDetailsGig.releasedAt
                    ? `Released on ${formatDateTime(selectedDetailsGig.releasedAt)}`
                    : selectedDetailsGig.fundedAt
                      ? `Funded on ${formatDateTime(selectedDetailsGig.fundedAt)}`
                      : selectedDetailsGig.volunteer
                        ? "Volunteer opportunities do not use Stripe."
                        : "This paid gig has not recorded a Stripe status yet."}
              </p>
            </div>
            <div className="rounded-2xl bg-myteengig-background px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Location</p>
              <p className="mt-2 text-sm font-medium text-myteengig-text-primary">{toLocationLine(selectedDetailsGig) || "Location TBD"}</p>
            </div>
	            <div className="rounded-2xl bg-myteengig-background px-4 py-3 xl:col-span-2">
	              <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Description</p>
	              <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
	                {selectedDetailsGig.gigDescription ?? "No description yet."}
	              </p>
	            </div>
	            {selectedDetailsGig.gigRequiresEquipment ? (
	              <div className="rounded-2xl bg-myteengig-background px-4 py-3 xl:col-span-2">
	                <p className="text-xs uppercase tracking-wide text-myteengig-text-muted">Equipment or supplies</p>
	                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-primary">
	                  {selectedDetailsGig.gigEquipmentDescription?.trim() || "Equipment is required, but details were not provided."}
	                </p>
	              </div>
	            ) : null}
	          </div>
        ) : null}
      </Modal>

      <GigRequestConversationModal
        isOpen={selectedConversationGig !== null}
        onClose={() => setSelectedConversationGig(null)}
        title={selectedConversationGig ? `${selectedConversationGig.gigTitle ?? "Gig"} · Message / Schedule` : "Message / Schedule"}
        role={role}
        requestId={selectedConversationGig?.requestId ?? ""}
        participantLabel={role === "adult" ? "teen" : "gig poster"}
        perspective={role}
        proposals={selectedConversationGig?.scheduleProposals}
        timeZone={selectedConversationGig?.gigTimeZone}
        summary={selectedConversationGig ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-myteengig-text-primary">Confirmed time</p>
            <p className="text-sm text-myteengig-text-muted">
              {formatDateTimeInGigTimeZone(selectedConversationGig.confirmedStartTime, selectedConversationGig.gigTimeZone)} to {formatDateTimeInGigTimeZone(selectedConversationGig.confirmedEndTime, selectedConversationGig.gigTimeZone)}
            </p>
            <p className="text-sm text-myteengig-text-muted">
              {`This keeps the message thread and the full scheduling history together for this ${scheduledUnit(selectedConversationGig)}.`}
            </p>
            {role === "adult" ? (
              <p className="text-sm text-myteengig-text-muted">
                {`Please coordinate scheduling details and send the teen your address so they can come complete this ${isMultiTeenAssignment(selectedConversationGig) ? "assignment" : "gig"}.`}
              </p>
            ) : null}
          </div>
        ) : null}
        emptyThreadMessage={`No messages or timing updates were recorded for this ${selectedConversationGig && isMultiTeenAssignment(selectedConversationGig) ? "assignment" : "gig"}.`}
        messagesDescription={`Messages and earlier timing changes stay together in this ${selectedConversationGig ? scheduledUnit(selectedConversationGig) : "scheduled gig"} thread.`}
        allowContactInfo
      />

      <Modal
        isOpen={selectedActionsGig !== null}
        onClose={() => {
          setSelectedActionsGig(null);
          setExpandedIssueRequestId(null);
        }}
        title={selectedActionsGig ? `${selectedActionsGig.gigTitle ?? "Gig"} · Actions` : "Actions"}
        panelClassName="max-w-3xl"
      >
        {selectedActionsGig ? (() => {
          const gig = selectedActionsGig;
          const issueIsOpen = expandedIssueRequestId === gig.requestId;
          const isPending = pendingRequestId === gig.requestId;
          const multiTeenAssignment = isMultiTeenAssignment(gig);
          const unit = multiTeenAssignment ? "assignment" : "gig";
          const completionHint = `This ${unit} is completed once both users mark it complete.`;
          const canAdultConfirm = gig.adultConfirmedCompletedAt === null;
          const teenButtonDisabled = isPending || gig.teenMarkedCompletedAt !== null;
          const arrivalBlockingReason = getArrivalBlockingReason(gig);
          const teenArrivalButtonDisabled = isPending || arrivalBlockingReason !== null;
          const adultButtonDisabled = isPending || !canAdultConfirm;
          const teenArrivalRecorded = gig.teenArrivedAt !== null;
          const started = hasStarted(gig);
          const lateCancellationWindow = isWithinLateCancellationWindow(gig);
          const canLeaveGig =
            role === "teen"
            && onLeaveGig
            && !started
            && !lateCancellationWindow
            && gig.teenArrivedAt === null
            && gig.teenMarkedCompletedAt === null
            && gig.adultConfirmedCompletedAt === null
            && gig.issueReportedAt === null;
          const canCancelGig =
            onCancel
            && (!started && !teenArrivalRecorded)
            && (role === "adult" || lateCancellationWindow)
            && (role === "adult" || gig.teenMarkedCompletedAt === null)
            && gig.adultConfirmedCompletedAt === null
            && gig.issueReportedAt === null;
          const removeTeenBlockingReason = getRemoveTeenBlockingReason(gig);
          const removeTeenDisabled = isPending || removeTeenBlockingReason !== null;
          const cancelTooltip = role === "adult"
            ? lateCancellationWindow
              ? "Within 24 hours of the confirmed start, cancellation is sent to admin review. Add a cancellation note. Admin can approve the refund, keep a $10 late fee, or pay the teen 3% for preparation."
              : started
                ? "After the scheduled start time, cancellation must go through issue review."
                : multiTeenAssignment
                  ? "Cancel removes this assignment from both schedules. Paid assignments refund the adult unless support review is required."
                  : "Cancel removes the gig from both schedules. Paid gigs refund the adult unless support review is required."
            : lateCancellationWindow
              ? "Within 24 hours of the confirmed start, cancellation is sent to admin review. Add a cancellation note so admin can decide the final outcome."
              : "Cancel is only available within 24 hours of the confirmed start time.";
          const adultActionDescription = multiTeenAssignment
            ? "Mark this assignment complete when you are satisfied with the teen's work, report an issue if this assignment needs review, or cancel this assignment if it should not move forward."
            : "Mark the gig complete when you are satisfied with the work, report an issue if something needs review, or cancel if the gig should not move forward.";
          const teenActionDescription = multiTeenAssignment
            ? "Mark this assignment complete when the work is finished, report an issue if it still needs review, or cancel if plans change before anyone marks completion."
            : "Mark the gig complete when the work is finished, report an issue if something still needs review, or cancel if plans change before anyone marks completion.";

          return (
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">Actions</p>
                <p className="mt-3 text-sm text-myteengig-text-muted">
                  {role === "adult" ? adultActionDescription : teenActionDescription}
                </p>
                {assignmentContext(gig) ? (
                  <p className="mt-2 text-xs font-medium text-myteengig-text-muted">{assignmentContext(gig)}</p>
                ) : null}
              </div>
              <div className="flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
                {role === "teen" && onTeenMarkArrived ? (
                  <span
                    className="block w-full sm:w-auto"
                    title={!isPending && teenArrivalButtonDisabled ? arrivalBlockingReason ?? undefined : undefined}
                  >
                    <Button
                      variant={teenArrivalRecorded ? "outline" : "primary"}
                      className="w-full sm:w-auto"
                      disabled={teenArrivalButtonDisabled}
                      onClick={() => void handleTeenArrival(gig.requestId)}
                    >
                      {teenArrivalRecorded ? "Arrival Recorded" : "I've Arrived"}
                    </Button>
                  </span>
                ) : null}
                <span
                  className="block w-full sm:w-auto"
                  title={!isPending && (role === "adult" ? adultButtonDisabled : teenButtonDisabled) ? completionHint : undefined}
                >
                  <Button
                    className="w-full sm:w-auto"
                    disabled={role === "adult" ? adultButtonDisabled : teenButtonDisabled}
                    onClick={() => role === "adult" ? void handleAdultConfirm(gig.requestId) : void handleTeenComplete(gig.requestId)}
                  >
                    {role === "adult"
                      ? gig.adultConfirmedCompletedAt ? "Marked Complete" : `Mark ${multiTeenAssignment ? "Assignment" : "Gig"} Complete`
                      : gig.teenMarkedCompletedAt ? "Marked Complete" : `Mark ${multiTeenAssignment ? "Assignment" : "Gig"} Complete`}
                  </Button>
                </span>
                <Button
                  variant="outline"
                  disabled={isPending}
                  onClick={() => setExpandedIssueRequestId((current) => current === gig.requestId ? null : gig.requestId)}
                >
                  {issueIsOpen ? "Hide Issue Form" : "Report Issue"}
                </Button>
                {role === "adult" && onRemoveTeen ? (
                  <div
                    className="w-full sm:w-auto"
                    title={!isPending && removeTeenBlockingReason ? removeTeenBlockingReason : undefined}
                  >
                    <Button
                      variant="outline"
                      disabled={removeTeenDisabled}
                      title={!isPending && removeTeenBlockingReason ? removeTeenBlockingReason : undefined}
                      className="w-full border-amber-300 text-amber-800 hover:bg-amber-50 sm:w-auto"
                      onClick={() => {
                        setRemoveTeenNote("");
                        setSelectedActionsGig(null);
                        setPendingRemoveTeenGig(gig);
                      }}
                    >
                      Remove Teen
                    </Button>
                  </div>
                ) : null}
                {canLeaveGig ? (
                  <div className="w-full sm:w-auto">
                    <Button
                      variant="outline"
                      disabled={isPending}
                      className="w-full border-amber-300 text-amber-800 hover:bg-amber-50 sm:w-auto"
                      onClick={() => {
                        setLeaveGigNote("");
                        setSelectedActionsGig(null);
                        setPendingLeaveGig(gig);
                      }}
                    >
                      Leave {multiTeenAssignment ? "Assignment" : "Gig"}
                    </Button>
                  </div>
                ) : null}
                {canCancelGig ? (
                  <span className="block w-full sm:w-auto" title={cancelTooltip}>
                    <Button
                      variant="outline"
                      disabled={isPending}
                      className="w-full border-red-300 text-red-700 hover:bg-red-50 sm:w-auto"
                      onClick={() => {
                        setCancelNote("");
                        setSelectedActionsGig(null);
                        setPendingCancelGig(gig);
                      }}
                    >
                      {role === "teen"
                        ? `Cancel ${multiTeenAssignment ? "Assignment" : "Gig"}`
                        : gig.volunteer
                          ? `Cancel ${multiTeenAssignment ? "Assignment" : "Gig"}`
                          : `Cancel ${multiTeenAssignment ? "Assignment" : "and Refund"}`}
                    </Button>
                  </span>
                ) : null}
              </div>
              {issueIsOpen ? (
                <div className="rounded-2xl bg-myteengig-background p-4">
                  <label htmlFor={`issue-${gig.requestId}`} className="text-sm font-medium text-myteengig-text-primary">
                    Issue details
                  </label>
                  <textarea
                    id={`issue-${gig.requestId}`}
                    value={issueDrafts[gig.requestId] ?? ""}
                    onChange={(event) => setIssueDrafts((current) => ({ ...current, [gig.requestId]: event.target.value }))}
                    rows={4}
                    className="mt-2 w-full rounded-xl border border-myteengig-border bg-white px-3 py-2 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                    placeholder="Describe what needs review or follow-up."
                  />
                  <Button
                    className="mt-3 w-full"
                    disabled={isPending}
                    onClick={() => void handleReportIssue(gig.requestId)}
                  >
                    Submit Issue
                  </Button>
                </div>
              ) : null}
              {gig.issueReportedAt ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p className="font-semibold">Last reported issue</p>
                  <p className="mt-1">{gig.issueReportMessage ?? "No details provided."}</p>
                  <p className="mt-2 text-xs">{formatDateTime(gig.issueReportedAt)}</p>
                </div>
              ) : null}
            </div>
          );
        })() : null}
      </Modal>

      <Modal
        isOpen={pendingReviewGig !== null}
        onClose={closeReviewModal}
        title={pendingReviewGig ? `Review ${pendingReviewGig.gigTitle ?? "this gig"}` : "Leave Review"}
        panelClassName="max-w-lg"
      >
        {pendingReviewGig && pendingReviewMode ? (
          <div className="space-y-5">
            {reviewError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{reviewError}</div>
            ) : null}

            <div className="rounded-2xl bg-myteengig-background px-4 py-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">
                {pendingReviewGig.gigTitle ?? "Scheduled gig"}
              </p>
              <p className="mt-1 text-sm text-myteengig-text-muted">{pendingReviewGig.participantName}</p>
              <p className="mt-1 text-sm text-myteengig-text-muted">
                Scheduled {formatDateTimeInGigTimeZone(pendingReviewGig.confirmedStartTime, pendingReviewGig.gigTimeZone)}
              </p>
              {assignmentContext(pendingReviewGig) ? (
                <p className="mt-1 text-sm text-myteengig-text-muted">{assignmentContext(pendingReviewGig)}</p>
              ) : null}
            </div>

            {(pendingReviewMode === "teen" ? onTeenSubmitReview : onAdultSubmitReview) ? (
              <>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-4 text-sm text-emerald-950">
                  Your completion mark was saved. You can leave your review now, or skip it and do it later in Reviews.
                </div>

                <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                  Rating
                  <select
                    value={reviewRating}
                    onChange={(event) => setReviewRating(event.target.value)}
                    className="h-11 rounded-xl border border-myteengig-border bg-white px-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                  >
                    {ratingOptions.map((option) => (
                      <option key={option} value={option.toString()}>
                        {option.toFixed(1)} / 5
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-2 text-sm text-myteengig-text-muted">
                  Written feedback
                  <textarea
                    value={reviewMessage}
                    onChange={(event) => setReviewMessage(event.target.value)}
                    rows={5}
                    placeholder={
                      pendingReviewMode === "teen"
                        ? "Share how the adult handled communication, clarity, and follow-through."
                        : "Share how the teen handled communication, reliability, and work quality."
                    }
                    className="rounded-xl border border-myteengig-border bg-white px-3 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                  />
                </label>

                <PageActionBar align="end">
                  <Button
                    variant="outline"
                    onClick={closeReviewModal}
                    disabled={pendingRequestId === pendingReviewGig.requestId}
                    className="w-full sm:w-auto"
                  >
                    Skip Review for Now
                  </Button>
                  <Button
                    onClick={() =>
                      void submitReview(pendingReviewGig, pendingReviewMode, {
                        reviewContext: pendingReviewMode === "teen" ? "POSTER_EXPERIENCE" : "WORKER_PERFORMANCE",
                        rating: Number(reviewRating),
                        message: reviewMessage.trim().length > 0 ? reviewMessage.trim() : null,
                      })
                    }
                    disabled={pendingRequestId === pendingReviewGig.requestId}
                    className="w-full sm:w-auto"
                  >
                    {pendingRequestId === pendingReviewGig.requestId ? "Submitting..." : "Submit Review"}
                  </Button>
                </PageActionBar>
              </>
            ) : (
              <>
                <div className="rounded-2xl border border-myteengig-border bg-myteengig-background px-4 py-4 text-sm text-myteengig-text-muted">
                  Your completion mark was saved. Once the adult also marks this {isMultiTeenAssignment(pendingReviewGig) ? "assignment" : "gig"} complete, you can leave your review from the Reviews section.
                </div>

                <PageActionBar align="end">
                  <Button
                    variant="outline"
                    onClick={closeReviewModal}
                    disabled={pendingRequestId === pendingReviewGig.requestId}
                    className="w-full sm:w-auto"
                  >
                    Continue
                  </Button>
                </PageActionBar>
              </>
            )}
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={pendingCancelGig !== null}
        onClose={() => {
          if (pendingRequestId === null) {
            setPendingCancelGig(null);
            setCancelNote("");
          }
        }}
        title={pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "Cancel Scheduled Assignment?" : "Cancel Scheduled Gig?"}
        panelClassName="max-w-lg"
      >
        <div className="space-y-5">
          <div className="rounded-2xl bg-myteengig-background px-4 py-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">
              What happens when you cancel {pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "this assignment" : "a gig"}?
            </p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {pendingCancelGig && isWithinLateCancellationWindow(pendingCancelGig)
                ? `Because this is within 24 hours of the ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "assignment's" : "gig's"} confirmed start time, it must go to admin review. Add a cancellation note so admin can decide whether to approve a refund, keep a late fee, or pay the teen for preparation.`
                : role === "teen"
                  ? "This sends the cancellation to admin review so they can decide the final outcome."
                  : pendingCancelGig?.volunteer
                  ? `This removes the ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "assignment" : "gig"} from both schedules and marks it cancelled.`
                  : `This removes the ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "assignment" : "gig"} from both schedules and refunds the adult's payment in full.`}
            </p>
            {pendingCancelGig && assignmentContext(pendingCancelGig) ? (
              <p className="mt-2 text-xs font-medium text-myteengig-text-muted">{assignmentContext(pendingCancelGig)}</p>
            ) : null}
          </div>

          <p className="text-sm text-myteengig-text-muted">
            {role === "teen"
              ? `Are you sure you want to cancel this scheduled ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "assignment" : "gig"}?`
              : `Are you sure you want to cancel this scheduled ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "assignment" : "gig"} for both sides?`}
          </p>

          {(role === "adult" || (role === "teen" && pendingCancelGig && isWithinLateCancellationWindow(pendingCancelGig))) ? (
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-myteengig-text-primary">
                {pendingCancelGig && isWithinLateCancellationWindow(pendingCancelGig) ? "Cancellation note" : "Cancellation note (optional)"}
              </span>
              <textarea
                value={cancelNote}
                onChange={(event) => setCancelNote(event.target.value)}
                rows={4}
                className="w-full rounded-xl border border-myteengig-border bg-white px-3 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                placeholder={`Explain why the ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "assignment" : "gig"} needs to be cancelled.`}
              />
            </label>
          ) : null}

          <PageActionBar align="end">
            <Button
              variant="outline"
              disabled={pendingRequestId !== null}
              onClick={() => {
                setPendingCancelGig(null);
                setCancelNote("");
              }}
              className="w-full sm:w-auto"
            >
              Keep {pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "Assignment" : "Gig"}
            </Button>
            <Button
              disabled={pendingRequestId !== null}
              onClick={() => void handleCancel()}
              className="w-full sm:w-auto"
            >
              {pendingRequestId !== null
                ? "Cancelling..."
                : pendingCancelGig && isWithinLateCancellationWindow(pendingCancelGig)
                  ? "Send for Admin Review"
                  : role === "teen"
                    ? `Yes, Cancel ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "Assignment" : "Gig"}`
                  : pendingCancelGig?.volunteer
                    ? `Yes, Cancel ${pendingCancelGig && isMultiTeenAssignment(pendingCancelGig) ? "Assignment" : "Gig"}`
                    : pendingCancelGig && isMultiTeenAssignment(pendingCancelGig)
                      ? "Yes, Cancel Assignment"
                      : "Yes, Cancel and Refund"}
            </Button>
          </PageActionBar>
        </div>
      </Modal>

      <Modal
        isOpen={pendingLeaveGig !== null}
        onClose={() => {
          if (pendingRequestId === null) {
            setPendingLeaveGig(null);
            setLeaveGigNote("");
          }
        }}
        title={pendingLeaveGigBlockedReason ? "Leave Gig Unavailable" : pendingLeaveGig && isMultiTeenAssignment(pendingLeaveGig) ? "Leave Assignment?" : "Leave Gig?"}
        panelClassName="max-w-lg"
      >
        <div className="space-y-5">
          <div className="rounded-2xl bg-myteengig-background px-4 py-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">
              What happens when you leave {pendingLeaveGig && isMultiTeenAssignment(pendingLeaveGig) ? "this assignment" : "a gig"}?
            </p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {pendingLeaveGig && isMultiTeenAssignment(pendingLeaveGig)
                ? "This removes you from this scheduled assignment, keeps the held funds, and reopens the spot so the adult can match with another teen."
                : "This removes you from the scheduled gig, keeps the held funds, and reopens the gig so the adult can match with another teen."}
            </p>
            {pendingLeaveGig && assignmentContext(pendingLeaveGig) ? (
              <p className="mt-2 text-xs font-medium text-myteengig-text-muted">{assignmentContext(pendingLeaveGig)}</p>
            ) : null}
          </div>
          {pendingLeaveGigBlockedReason ? (
            <>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                <p className="text-sm font-semibold text-amber-900">Why you can&apos;t leave this gig right now</p>
                <p className="mt-2 text-sm text-amber-800">{pendingLeaveGigBlockedReason}</p>
              </div>

              <PageActionBar align="end">
                <Button
                  variant="outline"
                  disabled={pendingRequestId !== null}
                  onClick={() => {
                    setPendingLeaveGig(null);
                    setLeaveGigNote("");
                  }}
                  className="w-full sm:w-auto"
                >
                  Close
                </Button>
              </PageActionBar>
            </>
          ) : (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-myteengig-text-primary">Leave note</span>
                <textarea
                  value={leaveGigNote}
                  onChange={(event) => setLeaveGigNote(event.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-3 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                  placeholder={`Document why you need to leave this scheduled ${pendingLeaveGig && isMultiTeenAssignment(pendingLeaveGig) ? "assignment" : "gig"}.`}
                />
              </label>

              <PageActionBar align="end">
                <Button
                  variant="outline"
                  disabled={pendingRequestId !== null}
                  onClick={() => {
                    setPendingLeaveGig(null);
                    setLeaveGigNote("");
                  }}
                  className="w-full sm:w-auto"
                >
                  Stay on {pendingLeaveGig && isMultiTeenAssignment(pendingLeaveGig) ? "Assignment" : "Gig"}
                </Button>
                <Button
                  disabled={pendingRequestId !== null || pendingLeaveGigBlockedReason !== null}
                  onClick={() => void handleLeaveGig()}
                  className="w-full sm:w-auto"
                >
                  {pendingRequestId !== null ? "Leaving..." : `Leave ${pendingLeaveGig && isMultiTeenAssignment(pendingLeaveGig) ? "Assignment" : "Gig"}`}
                </Button>
              </PageActionBar>
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={pendingRemoveTeenGig !== null}
        onClose={() => {
          if (pendingRequestId === null) {
            setPendingRemoveTeenGig(null);
            setRemoveTeenNote("");
          }
        }}
        title={pendingRemoveTeenBlockedReason ? "Remove Teen Unavailable" : "Remove Teen?"}
        panelClassName="max-w-lg"
      >
        <div className="space-y-5">
          <div className="rounded-2xl bg-myteengig-background px-4 py-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">What happens when you remove a teen?</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {pendingRemoveTeenGig && isMultiTeenAssignment(pendingRemoveTeenGig)
                ? "This removes the teen from this scheduled assignment, keeps the held funds, and reopens the spot for another teen."
                : "This removes the teen from the scheduled gig, keeps the held funds, and reopens the gig for another teen."}
            </p>
            {pendingRemoveTeenGig && assignmentContext(pendingRemoveTeenGig) ? (
              <p className="mt-2 text-xs font-medium text-myteengig-text-muted">{assignmentContext(pendingRemoveTeenGig)}</p>
            ) : null}
          </div>
          {pendingRemoveTeenBlockedReason ? (
            <>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                <p className="text-sm font-semibold text-amber-900">Why you can&apos;t remove this teen right now</p>
                <p className="mt-2 text-sm text-amber-800">{pendingRemoveTeenBlockedReason}</p>
              </div>

              <PageActionBar align="end">
                <Button
                  variant="outline"
                  disabled={pendingRequestId !== null}
                  onClick={() => {
                    setPendingRemoveTeenGig(null);
                    setRemoveTeenNote("");
                  }}
                  className="w-full sm:w-auto"
                >
                  Close
                </Button>
              </PageActionBar>
            </>
          ) : (
            <>
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-myteengig-text-primary">Removal note</span>
                <textarea
                  value={removeTeenNote}
                  onChange={(event) => setRemoveTeenNote(event.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-3 py-3 text-sm text-myteengig-text-primary outline-none transition focus:border-myteengig-primary"
                  placeholder={`Document why the teen is being removed from this scheduled ${pendingRemoveTeenGig && isMultiTeenAssignment(pendingRemoveTeenGig) ? "assignment" : "gig"}.`}
                />
              </label>

              <PageActionBar align="end">
                <Button
                  variant="outline"
                  disabled={pendingRequestId !== null}
                  onClick={() => {
                    setPendingRemoveTeenGig(null);
                    setRemoveTeenNote("");
                  }}
                  className="w-full sm:w-auto"
                >
                  Keep Teen
                </Button>
                <Button
                  disabled={pendingRequestId !== null || pendingRemoveTeenBlockedReason !== null}
                  onClick={() => void handleRemoveTeen()}
                  className="w-full sm:w-auto"
                >
                  {pendingRequestId !== null ? "Removing..." : "Remove Teen"}
                </Button>
              </PageActionBar>
            </>
          )}
        </div>
      </Modal>
    </PageShell>
  );
};

export default ScheduledGigsWorkspace;
